/**
 * Thanksgiving tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingTattoosPack: ToilePack = {
    slug: 'thanksgiving-tattoos',
    category: 'season',
    name: 'Thanksgiving tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'wheat', label: 'Wheat stalk', subject: 'A single wheat stalk'},
        {key: 'acorn', label: 'Acorn', subject: 'A small acorn with a leaf'},
        {key: 'leaf', label: 'Maple leaf', subject: 'A maple leaf'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'fine single-needle black line work, thin uniform stroke, no shading, no color', palette: 'Black ink only', framing: 'Small scale, centered with a wide white margin'},
        {key: 'traditional', label: 'Bold traditional', style: 'bold American traditional flash-sheet outline, thick uniform black line, no shading, no color', palette: 'Black ink only', framing: 'Centered with a wide white margin, classic flash-sheet scale'},
        {key: 'dotwork', label: 'Dotwork', style: 'fine dotwork stippled shading over a thin black outline, stippled dot-gradient fill, no color', palette: 'Black ink, stippled dot shading', framing: 'Centered with a wide white margin'},
    ],
    directions: ['a matching pair design', 'as a tiny minimalist mark', 'a pumpkin instead'],
    directionHint: 'try: a matching pair design',
    seo: {
        title: 'Thanksgiving tattoos: wheat, acorn, maple leaf',
        metaDescription: 'Nine fall tattoo designs — a wheat stalk, an acorn, a maple leaf — in fine line, bold traditional and dotwork, no skin, ready to reference.',
        h1: 'Fall tattoo designs: wheat stalk, acorn and maple leaf',
        tagline: 'Nine designs, three autumn motifs, three tattoo techniques each — drawn as a tattoo, not a picture.',
        intro: 'A fall tattoo drawn as a tattoo, not a picture: a single wheat stalk, a small acorn with its leaf, a maple leaf, each in fine single-needle line, bold American traditional outline, or stippled dotwork shading. Isolated on white with no skin or arm, so it reads as a design reference. Ask for a different autumn motif, like a matching pair or a pumpkin, and it redraws in the same three techniques.',
        useCases: [
            'a stencil reference for an autumn design',
            'a print for comparing shading styles',
            'a reference sheet for a tattoo consultation',
            'a starting point for a small nature tattoo',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each design is centered on a square page with a wide white margin, small tattoo-flash scale.'},
            {q: 'Are these ready to tattoo?', a: 'They are designs, not stencils — an artist redraws the outline at placement size before transferring it.'},
            {q: 'Can I get a different autumn motif?', a: "Yes — that's what the direction field is for. Type a different motif, like a pumpkin or a matching pair, and it redraws in the same three techniques."},
            {q: "What's the difference between the three techniques?", a: 'Fine line is a thin single-needle outline, bold traditional is a heavy flash-sheet line, and dotwork shades with stippled dots — three ways shops actually ink an autumn piece.'},
        ],
    },
}
